import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserCircleIcon, DevicePhoneMobileIcon, UsersIcon, CreditCardIcon,
  BuildingOffice2Icon, KeyIcon, SwatchIcon, GlobeAltIcon, CommandLineIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import userApi from '../../services/api/user.api';
import { updateUser } from '../../store/slices/auth.slice';
import { usePermissions } from '../../hooks/usePermissions';

export default function SettingsPage() {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { canManageApplications, canManageTeams, canManageBilling, canManageWhiteLabel } = usePermissions();

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>((user as any)?.avatarUrl || (user as any)?.avatar_url || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lê a imagem, redimensiona p/ 256px e gera um data URL leve (JPEG).
  const handleAvatarFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t('settings:profile.invalidImage', 'Selecione um arquivo de imagem.') });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // crop central quadrado
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        setAvatarUrl(canvas.toDataURL('image/jpeg', 0.85));
        setMessage(null);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setMessage(null);
  };

  const handleSave = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setMessage({ type: 'error', text: t('settings:profile.requiredFields') });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      const response = await userApi.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        avatarUrl: avatarUrl,
      });

      if (response.success && response.data) {
        // Convert snake_case from API to camelCase for Redux store
        const updatedUser = {
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          avatarUrl: response.data.avatar_url ?? avatarUrl ?? null,
        };

        // Update Redux store with new user data
        dispatch(updateUser(updatedUser));

        setMessage({ type: 'success', text: t('settings:profile.updateSuccess') });
      } else {
        setMessage({ type: 'error', text: t('settings:profile.updateError') });
      }
    } catch (err: any) {
      console.error('Profile update error:', err);
      setMessage({ type: 'error', text: err.response?.data?.error || t('settings:profile.updateError') });
    } finally {
      setIsSaving(false);
    }
  };

  const allMenuItems = [
    { name: t('settings:menu.profile'), path: '/settings/profile', Icon: UserCircleIcon, requiresPermission: false },
    { name: t('settings:menu.applications'), path: '/settings/applications', Icon: DevicePhoneMobileIcon, requiresPermission: canManageApplications },
    { name: t('settings:menu.team'), path: '/settings/users', Icon: UsersIcon, requiresPermission: canManageTeams },
    { name: t('settings:menu.billing'), path: '/settings/billing', Icon: CreditCardIcon, requiresPermission: canManageBilling },
    { name: t('settings:menu.company'), path: '/settings/company', Icon: BuildingOffice2Icon, requiresPermission: canManageBilling },
    { name: t('settings:menu.apiKeys'), path: '/settings/api-keys', Icon: KeyIcon, requiresPermission: false },
    { name: t('settings:menu.whiteLabel'), path: '/settings/white-label', Icon: SwatchIcon, requiresPermission: canManageWhiteLabel },
    { name: t('settings:menu.platforms'), path: '/settings/platforms', Icon: GlobeAltIcon, requiresPermission: false },
    { name: 'API REST + MCP', path: '/settings/mcp', Icon: CommandLineIcon, requiresPermission: false },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => item.requiresPermission !== false ? item.requiresPermission : true);

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('settings:subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const active = location.pathname === item.path;
              const Icon = item.Icon;
              return (
                <button
                  key={item.name}
                  onClick={() => !item.disabled && navigate(item.path)}
                  disabled={item.disabled}
                  aria-current={active ? 'page' : undefined}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    item.disabled
                      ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                      : active
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} />
                  <span className="flex-1 text-left">{item.name}</span>
                  {item.disabled && (
                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content - Profile Form */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
              {t('settings:profile.title')}
            </h2>

            {message && (
              <div className={`mb-4 rounded-lg p-4 ${message.type === 'success' ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
                <p className="text-sm font-medium">{message.text}</p>
              </div>
            )}

            {/* Avatar */}
            <div className="mb-6 flex items-center gap-5 border-b border-gray-200 pb-6 dark:border-gray-800">
              <div className="relative">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="h-20 w-20 rounded-full object-cover ring-2 ring-gray-200 dark:ring-gray-700" />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-2xl font-bold text-white">
                    {(formData.firstName?.[0] || '').toUpperCase()}{(formData.lastName?.[0] || '').toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarFile} className="hidden" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                    {t('settings:profile.changePhoto', 'Alterar foto')}
                  </button>
                  {avatarUrl && (
                    <button type="button" onClick={() => { setAvatarUrl(null); setMessage(null); }} className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
                      {t('settings:profile.removePhoto', 'Remover')}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings:profile.photoHint', 'JPG ou PNG. Clique em salvar para aplicar.')}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:profile.firstName')}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:profile.lastName')}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:profile.email')}
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  disabled
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {t('settings:profile.emailCannotChange')}
                </p>
              </div>
              <div className="pt-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? t('settings:profile.saving') : t('settings:profile.saveChanges')}
                </button>
              </div>
            </div>
          </div>

          {/* Quick Access Cards */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <button
              onClick={() => navigate('/settings/users')}
              className="rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/30"><UsersIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" /></span>
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings:quickAccess.team.title')}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings:quickAccess.team.description')}
              </p>
            </button>

            <button
              onClick={() => navigate('/settings/applications')}
              className="rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 dark:bg-sky-900/30"><DevicePhoneMobileIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" /></span>
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings:quickAccess.applications.title')}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings:quickAccess.applications.description')}
              </p>
            </button>

            <button
              onClick={() => navigate('/settings/api-keys')}
              className="rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/30"><KeyIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" /></span>
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings:quickAccess.apiKeys.title')}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings:quickAccess.apiKeys.description')}
              </p>
            </button>

            <button
              onClick={() => navigate('/settings/billing')}
              className="rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-900/30"><CreditCardIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /></span>
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings:quickAccess.billing.title')}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings:quickAccess.billing.description')}
              </p>
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
